import { Fragment, ReactElement, useCallback } from 'react';

import { components, GroupBase, MultiValueProps } from 'react-select';

import classNames from 'classnames';

import { isDefined, isNotDefined } from '@hsp/utilities';

import { Hidden } from 'components/select/shared/components/multi-value/components';
import cssClasses from 'components/select/shared/components/multi-value/multi-value.module.scss';

export const useMultiValueConfiguration = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  children,
  selectProps,
  ...props
}: MultiValueProps<Option, IsMulti, Group>) => {
  const { components: multiValueComponents, data, ...newProps } = props;
  const { isFixed, maxVisible } = selectProps;
  const { index, getValue } = newProps;

  const isItemFixed = useCallback(
    (value: Option | Group) => {
      return isFixed?.(value) ?? false;
    },
    [isFixed]
  );

  const hidden = useCallback(() => {
    const length = getValue().length;

    if (isNotDefined(maxVisible)) {
      return 0;
    }

    return Math.max(length - maxVisible, 0);
  }, [getValue, maxVisible]);

  const DefaultComponent = (
    <components.MultiValue
      {...newProps}
      className={classNames({ [cssClasses.isFixed]: isItemFixed(data) }, props.className)}
      components={{
        ...multiValueComponents,
        Remove: (multiValueRemoveProps) =>
          isItemFixed(data) ? <Fragment /> : <multiValueComponents.Remove {...multiValueRemoveProps} />,
      }}
      data={data}
      selectProps={selectProps}>
      {children}
    </components.MultiValue>
  );
  let Component: ReactElement | null = DefaultComponent;

  if (isDefined(maxVisible)) {
    if (index < maxVisible) {
      Component = DefaultComponent;
    } else if (index === maxVisible) {
      Component = <Hidden count={hidden()} />;
    } else {
      Component = null;
    }
  }

  return {
    Component,
  };
};
